import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import { NonNullableFormBuilder } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { AddressSummaryService } from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { ExternalAddressService } from "@onsip/common/services/api/resources/externalAddress/external-address.service";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { BehaviorSubject, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { ExternalSipAddressFormComponent } from "../classes/external-sip-address-form";
import { ExternalAddressExtended } from "../interfaces/external-address-extended";

@Component({
  selector: "onsip-external-sip-address-shelf-edit",
  templateUrl: "./external-sip-address-shelf-edit.component.html",
  styleUrls: ["../external-sip-address-shelves.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ExternalSipAddressShelfEditComponent
  extends ExternalSipAddressFormComponent
  implements OnInit, OnDestroy
{
  @Input() externalSip!: ExternalAddressExtended;
  @Output() cancelEditing = new EventEmitter<void>();

  protected unsubscriber = new Subscription();
  E2eLocators = E2eLocators;
  loadingStateComplete$ = new BehaviorSubject(true);

  constructor(
    protected addressSummary: AddressSummaryService,
    protected translate: TranslateService,
    protected fb: NonNullableFormBuilder,
    private dialog: MatDialog,
    private externalAddressService: ExternalAddressService,
    private snackbar: SnackbarService
  ) {
    super(addressSummary, translate, fb);
  }

  ngOnInit(): void {
    this.initFormValue();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.unsubscriber.unsubscribe();
  }

  async onSubmit(): Promise<void> {
    if (this.addressForm.invalid) {
      return;
    }
    this.loadingStateComplete$.next(false);
    this.extensionsListRef.editExtensions(this.externalSip.username);

    const result = await this.externalAddressService.externalAddressEdit({
      ForeignAddress: this.foreignAddress.value,
      Address: this.externalSip.aor,
      Name: this.name.value,
      Username: this.sipAddress.value
    });

    if (result.status === "error") {
      this.snackbar.openSnackBar(result.data.message, "error");
      this.externalAddressService.clearErrors();
      return;
    } else {
      this.snackbar.openSnackBar(
        this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
        "success"
      );
      this.loadingStateComplete$.next(true);
    }

    this.cancelEditing.next();
  }

  onCancel(): void {
    const modal = this.dialog.open(ModalMaterialComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      data: {
        title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
        message: this.translate.instant("ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"),
        primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
        secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
        primaryBtnFlat: true
      }
    });

    this.unsubscriber.add(
      modal
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj && retObj.doPrimaryAction) {
            this.cancelEditing.next();
          }
        })
    );
  }

  private initFormValue(): void {
    this.addressForm.patchValue({
      name: this.externalSip.name,
      foreignAddress: this.externalSip.foreignAddress,
      sipAddress: this.externalSip.username,
      extensions: this.externalSip.extensions
    });
  }
}
